import React from "react";
import { Card } from "@/ds";
import type { Fact } from "@/api";
import { useT } from "../../i18n";
import { customerAddressViews, type CustomerAddressView } from "./customer-addresses.logic";
import styles from "./customer-addresses.module.css";

/**
 * Where the invoice goes and where the food arrives (#805), side by side and never mixed.
 *
 * Read-only on purpose: these are facts, and a fact is corrected on the Fakten tab, under its
 * evidence, where the operator can see what the customer actually wrote. A second editor here
 * would be a second place to change the same record.
 */
function AddressBlock({ address }: { address: CustomerAddressView }) {
  const t = useT();
  const known = address.lines.length > 0;
  return (
    <div
      className={styles.address}
      data-testid={`customer-address-${address.kind}`}
      data-known={known ? "yes" : "no"}
    >
      <div className="neo-eyebrow">{t(address.labelKey)}</div>
      {known
        ? (
          <address className={styles.value}>
            {address.lines.map((line, index) => (
              <React.Fragment key={`${line}-${index}`}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </address>
        )
        : (
          <p className={styles.unknown}>
            {t(address.undecided ? "ws.customer.addressUndecided" : "ws.customer.addressUnknown")}
          </p>
        )}
      {address.statusKey && (
        <p className={styles.status}>{t(address.statusKey)}</p>
      )}
    </div>
  );
}

export function CustomerAddresses({ facts, embedded = false }: { facts: Fact[]; embedded?: boolean }) {
  const t = useT();
  const addresses = React.useMemo(() => customerAddressViews(facts), [facts]);
  const content = (
    <div className={styles.grid} data-testid="customer-addresses">
      {addresses.map((address) => <AddressBlock key={address.kind} address={address} />)}
    </div>
  );
  return embedded ? (
    <section className="cust-profile__addresses" aria-label={t("ws.customer.addressesTitle")}>
      <h3 className="cust-profile__heading">{t("ws.customer.addressesTitle")}</h3>
      {content}
    </section>
  ) : <Card title={t("ws.customer.addressesTitle")}>{content}</Card>;
}
